<template>
  <Teleport to="body">
    <Transition name="lead-modal">
      <div v-if="isOpen" :class="OVERLAY" @pointerdown="onOverlayPointerDown">
        <div
          ref="dialogRef"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="title != null ? `${id}-title` : undefined"
          :aria-describedby="subtitle != null ? `${id}-subtitle` : undefined"
          tabindex="-1"
          :class="mergedDialogClass"
          v-bind="restAttrs"
          @pointerdown.stop>
          <!-- 74px header on 20px inset. Fixed height (not padding) so it doesn't grow with content. -->
          <div
            class="tw:flex tw:h-[74px] tw:shrink-0 tw:items-center tw:gap-4 tw:border-b tw:border-solid tw:border-border-secondary tw:px-5">
            <component v-if="icon" :is="icon" :size="30" class="tw:shrink-0 tw:text-gray-900" />
            <div class="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-1">
              <h2
                v-if="title != null"
                :id="`${id}-title`"
                class="tw:m-0! tw:truncate tw:text-16! tw:font-semibold tw:text-gray-900">
                {{ title }}
              </h2>
              <p
                v-if="subtitle != null"
                :id="`${id}-subtitle`"
                class="tw:m-0! tw:truncate tw:text-14! tw:font-normal tw:text-gray-600">
                {{ subtitle }}
              </p>
            </div>
            <!-- Bare 24px glyph per DS (NOT a bordered ghost button). -m-1 pulls the 4px hit-area padding
                 back out of the header's 20px inset. -->
            <button
              type="button"
              :aria-label="closeLabel"
              data-testid="lead-modal-close-button"
              :disabled="disableCloseButton"
              :class="[
                'tw:-m-1 tw:shrink-0 tw:rounded-sm! tw:border-0 tw:bg-transparent tw:p-1 tw:text-gray-500',
                'tw:transition-colors tw:duration-150 tw:ease-in-out',
                disableCloseButton
                  ? 'tw:cursor-not-allowed tw:opacity-40'
                  : 'tw:cursor-pointer tw:hover:bg-gray-100 tw:hover:text-gray-700',
                'tw:focus-visible:outline-none tw:focus-visible:shadow-[0_0_0_4px_rgb(0_122_255/0.1)]',
              ]"
              @click="close">
              <IconX :size="24" />
            </button>
          </div>

          <!-- Body: 20px inset unless `flush` for edge-to-edge panes. Scrolls inside the dialog. -->
          <div :class="cn('tw:min-h-0 tw:flex-1 tw:overflow-y-auto', !flush && 'tw:p-5')">
            <slot />
          </div>

          <!-- Footer: 64px on gray-50 with a top rule. Rendered only if the footer slot is used OR the
               convenience actions props are set. Content slot on left, actions on right. -->
          <div
            v-if="hasFooter"
            class="tw:flex tw:h-16 tw:shrink-0 tw:items-center tw:justify-between tw:gap-3 tw:border-t tw:border-solid tw:border-border-secondary tw:bg-gray-50 tw:px-5">
            <div class="tw:flex tw:min-w-0 tw:items-center tw:gap-2">
              <slot name="footer-start" />
            </div>
            <div class="tw:flex tw:shrink-0 tw:items-center tw:gap-2">
              <slot name="footer" :close="close">
                <!-- default footer: cancel + confirm buttons, driven by props -->
                <LeadButton v-if="cancelLabel" variant="ghost" data-testid="modal-cancel-button" @click="close">{{
                  cancelLabel
                }}</LeadButton>
                <LeadButton
                  v-if="confirmLabel"
                  data-testid="modal-confirm-button"
                  :variant="confirmVariant"
                  :loading="confirmLoading"
                  @click="onConfirm">
                  {{ confirmLabel }}
                </LeadButton>
              </slot>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
// LeadModal — the LEAD Design System's Modal. Behaviour and geometry mirror
// design-source/components/Modal/Modal.tsx (see that file for the "why" behind
// the 74px header, 64px footer, DS size map, scrim tint, and focus trap).
//
// Vue port choices:
//   - Teleport instead of createPortal: same effect, no extra dependency.
//   - `pointerdown` on the overlay (not click) so a text selection that drags
//     out onto the scrim doesn't count as a dismissal.
//   - Focus trap + scroll lock + ESC handling attached ONLY while open, torn
//     down on close, so a closed modal costs nothing.
import type { Component } from 'vue';
import { computed, nextTick, onBeforeUnmount, ref, useAttrs, useId, useSlots, watch } from 'vue';
import { IconX } from '@tabler/icons-vue';
import LeadButton, { type LeadButtonVariant } from '@/components/lead-design/LeadButton.vue';
import { cn } from '@/lib/cn';

defineOptions({ inheritAttrs: false });

export type LeadModalSize = 'sm' | 'md' | 'lg' | 'xl';

const props = withDefaults(
  defineProps<{
    /** Header headline. Also names the dialog for assistive tech via aria-labelledby. */
    title?: string;
    /** Optional single supporting line under the title. */
    subtitle?: string;
    /** Leading 30px glyph in the header (Tabler icon component). Most dialogs have none. */
    icon?: Component;
    size?: LeadModalSize;
    /** Drop the body's 20px padding — for content that draws its own panes edge to edge. */
    flush?: boolean;
    /** Accessible name for the close button. */
    closeLabel?: string;
    /** Disable dismissal via backdrop click. */
    disableBackdropClose?: boolean;
    /** Disable dismissal via ESC key. */
    disableEscClose?: boolean;
    /** Disable the header X close button. */
    disableCloseButton?: boolean;
    /** Default footer: cancel button label. Omit to hide the cancel button. */
    cancelLabel?: string;
    /** Default footer: confirm button label. Omit to hide the confirm button. */
    confirmLabel?: string;
    /** Default footer: confirm button variant. `danger` for destructive confirmations. */
    confirmVariant?: LeadButtonVariant;
    /** Default footer: show a spinner in the confirm button while an async action runs. */
    confirmLoading?: boolean;
  }>(),
  {
    title: undefined,
    subtitle: undefined,
    icon: undefined,
    size: 'md',
    flush: false,
    closeLabel: 'Close',
    disableBackdropClose: false,
    disableEscClose: false,
    disableCloseButton: false,
    cancelLabel: undefined,
    confirmLabel: undefined,
    confirmVariant: 'primary',
    confirmLoading: false,
  }
);

const isOpen = defineModel<boolean>({ required: true });

const emit = defineEmits<{
  (e: 'confirm'): void;
  (e: 'close'): void;
}>();

const slots = useSlots();
const attrs = useAttrs();
const id = useId();

/* ---------------------------------- STATE --------------------------------- */

const dialogRef = ref<HTMLDivElement | null>(null);
let previouslyFocused: HTMLElement | null = null;
let previousOverflow = '';

/* --------------------------------- COMPUTED ------------------------------- */

// z-[1100] beats the DashboardSidebar (z-index 1031/1032) and any other Tabler/Bootstrap
// legacy layer. The DS uses z-50, but that's meaningless while lithium.css is still shipped.
const OVERLAY =
  'tw:fixed tw:inset-0 tw:z-[1100] tw:flex tw:items-center tw:justify-center tw:bg-gray-900/40 tw:p-4 tw:backdrop-blur-xs';

// Base dialog shell. `flex-col` + `overflow-hidden` is the whole scroll model — header/footer are
// `shrink-0`, body is `flex-1 overflow-y-auto`, so long bodies scroll inside the dialog and chrome
// holds still. `overflow-hidden` also clips the footer's gray tint to the rounded corners.
const DIALOG_BASE =
  'tw:flex tw:max-h-[calc(100vh-2rem)] tw:w-full tw:flex-col tw:overflow-hidden tw:rounded-md! ' +
  'tw:border tw:border-solid tw:border-border-secondary tw:bg-surface tw:shadow-raised tw:focus:outline-none';

// Both ends bracket the two designed frames (461px, 1310px). `w-full` under `max-w-*` so the box
// shrinks to fit small viewports instead of overflowing them.
const SIZE_MAP: Record<LeadModalSize, string> = {
  sm: 'tw:max-w-[29rem]',
  md: 'tw:max-w-[40rem]',
  lg: 'tw:max-w-[52rem]',
  xl: 'tw:max-w-[82rem]',
};

const mergedDialogClass = computed(() => cn(DIALOG_BASE, SIZE_MAP[props.size], attrs.class as string | undefined));

const restAttrs = computed(() => {
  const { class: _c, ...rest } = attrs;
  return rest;
});

const hasFooter = computed(
  () => !!slots.footer || !!slots['footer-start'] || !!props.cancelLabel || !!props.confirmLabel
);

/* --------------------------------- METHODS -------------------------------- */

function close() {
  if (!isOpen.value) return;
  isOpen.value = false;
  emit('close');
}

function onConfirm() {
  emit('confirm');
  // auto-close on confirm — matches ConfirmationDialog UX (dialog closes, action runs in bg)
  close();
}

function onOverlayPointerDown(event: PointerEvent) {
  if (props.disableBackdropClose) return;
  // pointerdown on the overlay itself — the dialog stops propagation on its own pointerdown, so this
  // fires only for genuine backdrop clicks.
  if (event.target === event.currentTarget) close();
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function onKeyDown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    if (!props.disableEscClose) close();
    return;
  }
  if (event.key !== 'Tab' || dialogRef.value == null) return;

  const focusable = Array.from(dialogRef.value.querySelectorAll<HTMLElement>(FOCUSABLE));
  if (focusable.length === 0) {
    event.preventDefault();
    dialogRef.value.focus();
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;

  if (event.shiftKey && (active === first || active === dialogRef.value)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (active === last || active === dialogRef.value)) {
    event.preventDefault();
    first.focus();
  }
}

function attachOpenSideEffects() {
  previouslyFocused = document.activeElement as HTMLElement | null;
  previousOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  document.addEventListener('keydown', onKeyDown);
  // dialog is teleported + transitioned; wait a tick for it to mount before focusing.
  nextTick(() => dialogRef.value?.focus());
}

function detachOpenSideEffects() {
  document.removeEventListener('keydown', onKeyDown);
  // restore the previous body overflow instead of clearing it — a host app that locks the body for
  // its own reasons shouldn't silently lose that when the dialog closes.
  document.body.style.overflow = previousOverflow;
  previouslyFocused?.focus?.();
  previouslyFocused = null;
}

/* --------------------------------- WATCH ---------------------------------- */

watch(
  isOpen,
  (open, wasOpen) => {
    if (open && !wasOpen) attachOpenSideEffects();
    else if (!open && wasOpen) detachOpenSideEffects();
  },
  { immediate: true }
);

// Safety net: if the parent unmounts this component while open, clean up too.
onBeforeUnmount(() => {
  if (isOpen.value) detachOpenSideEffects();
});
</script>

<style>
/* Scoped-safe global transitions for the teleported overlay. */
.lead-modal-enter-active,
.lead-modal-leave-active {
  transition: opacity 150ms ease-out;
}
.lead-modal-enter-from,
.lead-modal-leave-to {
  opacity: 0;
}
</style>
